import { george, observer, pr, React, RouteComponentProps, theme, ui } from "aaa";
import { ChooseEmailResponsive } from "components/common/ChooseEmail";
import { FormattedHTMLMessage } from "i18n/util";
import { useEffect } from "react";
import { addressToString } from "stores/ContactsStore";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { isNewCreditCard } from "stores/OffersStore";
import profileStore from "stores/ProfileStore";
import { pushRoute } from "stores/RouterFunctions";
import trackingStore from "stores/TrackingStore";
import * as amount from "types/amount";
import { Account } from "../common/Account";
import { CreditCardBusinessStore } from "./CreditCardBusinessStore";
import { ShopCreditCardBusinessRoutes } from "./ShopCreditCardBusinessRoutes";

type IProps = RouteComponentProps<{}> & {
    store: CreditCardBusinessStore
};

export const PurchaseSummary = observer(({ store }: { store: CreditCardBusinessStore }) => {
    if (profileStore.isLoading
        || store.contactsStore.isLoading
        || store.isLoading) {
        return <george.LoadingIndicator isLoading />;
    }

    const address = store.contactsStore.selectedAddress;

    const feesFirst = store.getProductDetailsForConditionShortCode("KA-KKERST");
    const fees = store.getProductDetailsForConditionShortCode("KA-KK");
    const channelProduct = externalInterfaceStore.channelProduct;
    const isNewCard = channelProduct && isNewCreditCard(channelProduct);

    return (
        <>
            <george.SectionTitle style={{ marginTop: 0 }}>
                {ui.__("common.summary")}
            </george.SectionTitle>

            <pr.LightSeparatorLine spacing={theme.MARGIN} />

            <george.ResponsiveSummaryElement label={ui.__("loan.signing.product.title")}>
                <pr.SigningLabel>{store.productName || "-"}</pr.SigningLabel>
            </george.ResponsiveSummaryElement>

            {store.cardFormFactor && (
                <george.ResponsiveSummaryElement label={ui.__("common.cardFormFactor")}>
                    <pr.SigningLabel>
                        {store.cardFormFactor === "physical" && ui.__("a11y.physicalCard")}
                        {store.cardFormFactor === "virtual" && ui.__("a11y.virtualCard")}
                    </pr.SigningLabel>
                </george.ResponsiveSummaryElement>
            )}

            <george.ResponsiveSummaryElement label={ui.__("common.monthlyCosts")}>
                <pr.SigningLabel>
                    {feesFirst ? amount.amountToCurrencyString(feesFirst.amount || amount.ZERO_AMOUNT) : "-"}{" "}
                    {feesFirst ? feesFirst.fullConditionName : "-"}
                </pr.SigningLabel>
                <pr.SigningLabel>
                    {fees ? amount.amountToCurrencyString(fees.amount || amount.ZERO_AMOUNT) : "-"}{" "}
                    {fees ? fees.fullConditionName : "-"}
                </pr.SigningLabel>
            </george.ResponsiveSummaryElement>


            <george.ResponsiveSummaryElement label={ui.__("shop.cardInfo.repayment.title")}>
                <pr.SigningLabel>{store.repaymentChargeCard ? ui.__("shop.cardInfo.repayment.chargeCard") : ui.__("shop.cardInfo.repayment.revolving")}</pr.SigningLabel>
            </george.ResponsiveSummaryElement>

            <george.ResponsiveSummaryElement label={ui.__("common.clearingAccount")}>
                <Account account={store.accountsStore.selectedAccount!} />
            </george.ResponsiveSummaryElement>

            <george.ResponsiveSummaryElement label={ui.__("common.cardholder")}>
                <pr.SigningLabel>{profileStore.name}</pr.SigningLabel>
            </george.ResponsiveSummaryElement>

            <george.ResponsiveSummaryElement label={ui.__("common.address")}>
                {address
                    ? <pr.SigningLabel>{addressToString(address)}</pr.SigningLabel>
                    : <pr.SigningLabel>{ui.__("shop.personalData.noAddress.message")}</pr.SigningLabel>
                }
            </george.ResponsiveSummaryElement>
            {store.isMilesAndMoreCreditCard && <george.ResponsiveSummaryElement label={<FormattedHTMLMessage id="shop.cardInfo.milesAndMore.serviceCardNumber.label" />}>
                <pr.SigningLabel>{store.milesAndMoreCardNumber}</pr.SigningLabel>
            </george.ResponsiveSummaryElement>}
        </>
    );
});

export const ShopCreditCardBusinessSummary = observer(({ store }: IProps) => {

    const [approved, setApproved] = React.useState(false);
    const [buttonDisabled, setButtonDisabled] = React.useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    const approvalDescription = <FormattedHTMLMessage id="shop.creditCardBusiness.summary.approval" />;
    const emailDescription = store.offer ? ui.__("shop.creditCardBusiness.summary.message", { product: store.productName }) : ui.__("shop.creditCardBusiness.summary.message.generic");

    const PageComponent = externalInterfaceStore.isGeorgeApp ? george.PageWithActionButton : React.Fragment;

    const handleChangeApprove = (_: any, checked: boolean) => setApproved(checked);

    const handleChangeEmail = (emailId: string) => {
        store.contractEmailId = emailId;
    }

    const handlePurchase = async () => {
        try {
            setButtonDisabled(true);
            await store.purchaseCreditCard();
            await trackingStore.trackEvent({ eventName: "orderInitiated" });
            pushRoute(ShopCreditCardBusinessRoutes.SIGNING);
        } catch (error) {
            pushRoute("/error");
        } finally {
            setButtonDisabled(false);
        }
    }
    return (
        <george.Background>
            <PageComponent>
                <george.Card style={{ marginBottom: theme.MARGIN_SMALL }}>
                    <pr.A11yPopupHider>
                        <div style={{ padding: theme.MARGIN }}>
                            <PurchaseSummary store={store} />
                        </div>
                        <pr.LightSeparatorLine spacing={theme.MARGIN} />
                    </pr.A11yPopupHider>

                    <div style={{ padding: theme.MARGIN }}>
                        <ChooseEmailResponsive
                            contactsStore={store.contactsStore}
                            description={emailDescription}
                            onEmailChanged={handleChangeEmail}
                            selectedEmailId={store.contractEmailId}
                        />
                    </div>

                    <pr.A11yPopupHider>
                        <pr.LightSeparatorLine spacing={theme.MARGIN} />
                        <div style={{ padding: theme.MARGIN }}>
                            <george.ApprovalEmbedded
                                message={approvalDescription}
                                onCheck={handleChangeApprove}
                            />
                        </div>
                        {!externalInterfaceStore.isGeorgeApp &&
                            <pr.LightSeparatorLine spacing={theme.MARGIN} />
                        }
                        <george.ActionButton
                            messageId="loan.summary.actionSign"
                            disableButton={buttonDisabled || !approved || !store.contactsStore.selectedEmailAddressId}
                            onClick={handlePurchase}
                            primary
                            {...george.getActionButtonWebVersionProps()}
                        />
                    </pr.A11yPopupHider>
                </george.Card>
            </PageComponent>
        </george.Background>
    );
})




// WEBPACK FOOTER //
// ./src/components/shopCreditCardBusiness/ShopCreditCardBusinessSummary.tsx